/* ── IncomeBazaar redesigned Discover deal card ──
   White panel, blush accents, brick-red action, ink-navy numerals.
   Numbers use bold sans tabular figures; headings/labels may use serif. */

.dealCardContainer {
  display: flex;
  width: 100%;
}

.dealCard {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--ib-panel);
  border: 1px solid var(--ib-line);
  border-radius: 13px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(27, 37, 52, 0.05), 0 6px 18px rgba(120, 30, 30, 0.05);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.dealCard:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(27, 37, 52, 0.09), 0 16px 34px rgba(120, 30, 30, 0.1);
}

/* Standard vs debt: both subtle. Standard keeps the faint blush line;
   debt gets an ink-navy hairline to stay distinct. */
.std {
  border: 1px solid var(--ib-line);
}

.debt {
  border: 1px solid var(--ib-navy-line);
}

/* Faded anchor-logo watermark filling the card, behind content. */
.watermark {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 78% auto;
  opacity: 0.05;
  pointer-events: none;
}

.dealCardContent {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* ── header row: foreground logo + status badge ── */
.cTop {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 16px 8px;
}

.logoBox {
  display: flex;
  align-items: center;
  min-height: 24px;
}

.dealLogo {
  height: 22px;
  width: auto;
  max-width: 130px;
  object-fit: contain;
  display: block;
}

.badge {
  margin-left: auto;
  flex: none;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 4px 9px;
  border-radius: 6px;
  text-transform: uppercase;
}

.badgeOpen {
  background: var(--ib-good-soft);
  color: var(--ib-good);
}

.badgeClosing {
  background: var(--ib-red-soft);
  color: var(--ib-red);
}

.badgeFull {
  background: var(--ib-chip);
  color: var(--ib-muted);
}

/* ── deal code: faint mono/tabular line ── */
.cCode {
  padding: 0 16px;
  font-size: 11px;
  color: var(--ib-faint);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.03em;
}

/* ── hero: Net IRR (big) + Tenure ── */
.cHero {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  padding: 9px 16px 13px;
}

.irr,
.tenure {
  display: flex;
  flex-direction: column;
}

.heroNum {
  font-family: var(--sans, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif);
  font-size: 33px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 0.9;
  color: var(--ib-ink);
  font-variant-numeric: tabular-nums lining-nums;
}

.heroUnit {
  font-size: 16px;
  font-weight: 700;
  color: var(--ib-muted);
  margin-left: 1px;
}

.heroNumSm {
  font-family: var(--sans, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--ib-ink);
  font-variant-numeric: tabular-nums lining-nums;
}

.heroUnitSm {
  font-size: 14px;
  font-weight: 700;
  color: var(--ib-muted);
  margin-left: 1px;
}

.heroLbl {
  font-size: 11px;
  color: var(--ib-muted);
  font-weight: 600;
  margin-top: 6px;
}

.heroSep {
  width: 1px;
  align-self: stretch;
  background: var(--ib-line);
  margin: 4px 0;
}

/* ── progress bar (escalating fill) ── */
.prog {
  padding: 0 16px 13px;
}

.track {
  height: 6px;
  border-radius: 6px;
  background: var(--ib-salmon-tint);
  overflow: hidden;
}

.fill {
  height: 100%;
  border-radius: 6px;
  background: var(--ib-navy);
}

.progCaption {
  display: flex;
  justify-content: space-between;
  margin-top: 7px;
  font-size: 11.5px;
  color: var(--ib-muted);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.progCaption b {
  color: var(--ib-ink);
}

/* ── facts strip ── */
.facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 14px;
  padding: 12px 16px 13px;
  border-top: 1px solid var(--ib-line-2);
}

.fact {
  display: flex;
  flex-direction: column;
}

.factRight {
  text-align: right;
}

.factKey {
  font-size: 11px;
  color: var(--ib-muted);
  font-weight: 600;
}

.factVal {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ib-ink);
  font-variant-numeric: tabular-nums lining-nums;
}
